import { notifications } from "@mantine/notifications";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MessageKey } from "../../shared/i18n";
import { useMessages } from "../i18n/provider";
import { ApiClientError, endpoints, isUnauthorized, type Me, type ResumeSummary } from "./client";

export const authConfigQuery = queryOptions({
  queryKey: ["auth-config"],
  queryFn: endpoints.authConfig,
  staleTime: 5 * 60_000,
});

export const meQuery = queryOptions<Me>({
  queryKey: ["me"],
  queryFn: endpoints.me,
  staleTime: 30_000,
  retry: (count, error) => !isUnauthorized(error) && count < 1,
});

export const resumesQuery = queryOptions<{ items: ResumeSummary[] }>({
  queryKey: ["resumes"],
  queryFn: endpoints.resumes,
  staleTime: 30_000,
});

export function documentQuery(id: string) {
  return queryOptions({
    queryKey: ["resume", id, "document"],
    queryFn: () => endpoints.document(id),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function shareQuery(id: string) {
  return queryOptions({
    queryKey: ["resume", id, "share"],
    queryFn: () => endpoints.share(id),
    staleTime: 30_000,
  });
}

export function publicCvQuery(slug: string) {
  return queryOptions({
    queryKey: ["public-cv", slug],
    queryFn: () => endpoints.publicResume(slug),
    staleTime: 60_000,
    retry: false,
  });
}

export function appPasswordsQuery() {
  return queryOptions({
    queryKey: ["app-passwords"],
    queryFn: endpoints.appPasswords,
    staleTime: 30_000,
  });
}

export function billingQuery() {
  return queryOptions({
    queryKey: ["billing"],
    queryFn: endpoints.billing,
    staleTime: 30_000,
  });
}

export const organizationsQuery = queryOptions({
  queryKey: ["organizations"],
  queryFn: endpoints.organizations,
  staleTime: 30_000,
});

export function organizationQuery(id: string) {
  return queryOptions({
    queryKey: ["organization", id],
    queryFn: () => endpoints.organization(id),
    staleTime: 30_000,
  });
}

export function invitationQuery(token: string) {
  return queryOptions({
    queryKey: ["invitation", token],
    queryFn: () => endpoints.invitationInfo(token),
    staleTime: 30_000,
    retry: false,
  });
}

export function useMe() {
  return useQuery(meQuery);
}

const ERROR_KEYS: Record<string, MessageKey> = {
  UNAUTHORIZED: "errors.unauthorized",
  NOT_FOUND: "errors.notFound",
  PRO_REQUIRED: "errors.proRequired",
  RESUME_LIMIT: "errors.resumeLimit",
  STORAGE_LIMIT: "errors.storageLimit",
  APP_PASSWORD_LIMIT: "errors.appPasswordLimit",
  VALIDATION_ERROR: "errors.validation",
  CONFLICT: "errors.slugTaken",
  FORBIDDEN: "errors.forbidden",
  ALREADY_SUBSCRIBED: "organization.alreadySubscribed",
  ORG_SUBSCRIPTION_REQUIRED: "errors.orgSubscription",
  ORG_SEAT_LIMIT: "errors.orgSeatLimit",
  LAST_OWNER: "errors.lastOwner",
  ORG_OWNER: "errors.orgOwner",
  ALREADY_MEMBER: "errors.alreadyMember",
  ALREADY_INVITED: "errors.alreadyInvited",
  INVITE_USED: "errors.inviteUsed",
  INVITE_EXPIRED: "errors.inviteExpired",
  PAYLOAD_TOO_LARGE: "errors.fileSize",
  UNSUPPORTED_MEDIA_TYPE: "errors.fileType",
};

export function useErrorMessage() {
  const t = useMessages();
  return (error: unknown): string => {
    if (error instanceof ApiClientError) {
      const key = ERROR_KEYS[error.code];
      if (key) return t(key);
    }
    if (error instanceof TypeError) return t("errors.network");
    return t("errors.generic");
  };
}

export function useNotifyError() {
  const message = useErrorMessage();
  return (error: unknown) => {
    notifications.show({
      color: "red",
      message: message(error),
      autoClose: 4000,
    });
  };
}

export function useDeleteResume() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpoints.deleteResume(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["resumes"] });
      void client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
